import React from "react";
import moment from "moment";

import DeviceDropdown from "./DeviceDropdown/DeviceDropdown";
import StatusNeedsCleaning from "./StatusNeedsCleaning/StatusNeedsCleaning";
import StatusNeedsAcidCleaning from "./StatusNeedsAcidCleaning/StatusNeedsAcidCleaning";
import StatusDisconnected from "./StatusDisconnected/StatusDisconnected";
import StatusUserAction from "./StatusUserAction/StatusUserAction";
import StatusFinishCleaning from "./StatusFinishCleaning/StatusFinishCleaning";
import StatusActive from "./StatusActive/StatusActive";
import StatusIdle from "./StatusIdle/StatusIdle";
import StatusIdleKeg from "./StatusIdleKeg/StatusIdleKeg";
import StatusUpdating from "./StatusUpdating/StatusUpdating";
import StatusSessionStarting from "./StatusSessionStarting/StatusSessionStarting";
import StatusBrewingDisconnected from "./StatusBrewingDisconnected/StatusBrewingDisconnected";
import StatusFermentationInterrupted from "./StatusFermentationInterrupted/StatusFermentationInterrupted";
import "./device-item.scss";
import { srmToCss } from "@util/beercalc";
import {
  isDeviceActiveDisconnected,
  isDeviceDisconnected,
  isDeviceStarting,
  isDeviceUpdating,
  deviceNeedsAcidCleaning,
  deviceNeedsCleaning,
  deviceFinishCleaning,
  deviceRequiresUserAction,
  isDeviceIdle,
  isDeviceCleaning,
  isDeviceActive,
  isDeviceKeg,
  isDeviceIdleProcess,
  kegFermentationInterrupted,
} from "@util/deviceStatusHelper";
import { activeOrchestrations } from "@brewery/services/fermentationOrchestrator";

const DeviceItem = ({
  device,
  showModal,
  showWifiModal,
  showDeviceModal,
  showCleaningModal,
  showStopSessionModal,
  showResetSessionModal,
  showEditFermentationModal,
  showUnregisterDeviceModal,
  brewEligible,
  nextBrewDate,
  // showAcidCleanModal, ENABLE ONCE ACID CLEANING IS SUPPORTED
  showAcidSupportModal,
}) => {
  const activeDisconnected = isDeviceActiveDisconnected(device);
  const isDisconnected = isDeviceDisconnected(device);
  const isStarting = isDeviceStarting(device);
  const isUpdating = isDeviceUpdating(device);
  const needsAcidCleaning = deviceNeedsAcidCleaning(device);
  const needsCleaning = deviceNeedsCleaning(device);
  // Parked at the end of a (acid) CIP waiting for the teardown ack. Deliberately
  // NOT gated on session_id: the backend's teardown guards keep the cleaning
  // session ACTIVE while the device sits here, so a session-less strand is only
  // one of the two shapes this state comes in. StatusFinishCleaning routes on
  // session_id -- carousel when there is a session, device-scoped ack when there
  // is not. Also checks process_type, which narrows the mismatch against the
  // backend's process_type + process_state + user_action triple (breweryoverview
  // does not expose process_state, so it cannot be matched exactly).
  //
  // The offline-mid-CIP guard lives in deviceFinishCleaning() itself (see
  // src/util/deviceStatusHelper.js): `isDisconnected` alone does not cover an
  // offline device mid-CIP, because PROC_CLEAN_MINIBREW puts it in
  // `activeDisconnected` instead.
  const finishCleaning = deviceFinishCleaning(device);
  const userAction = deviceRequiresUserAction(device);
  const isIdle = isDeviceIdle(device);
  const isCleaning = isDeviceCleaning(device);
  const isActive = isDeviceActive(device);
  const isKeg = isDeviceKeg(device);
  const isIdleProcess = isDeviceIdleProcess(device);
  // A healthy swap passes THROUGH the stranded signature between STOP-ack
  // and START-accept, so suppress the tile while this tab is driving an
  // orchestration on the keg.
  const fermentationInterrupted =
    kegFermentationInterrupted(device) &&
    !activeOrchestrations.has(device.uuid);

  const renderStatusFooter = () => {
    if (isUpdating) {
      return <StatusUpdating />;
    }

    if (activeDisconnected) {
      return (
        <StatusBrewingDisconnected showModal={() => showWifiModal(device)} />
      );
    }

    if (isDisconnected) {
      return <StatusDisconnected showModal={() => showWifiModal(device)} />;
    }

    if (isStarting) {
      return <StatusSessionStarting />;
    }

    if (finishCleaning) {
      return (
        <StatusFinishCleaning
          deviceUUID={device.uuid}
          sessionId={device.session_id}
        />
      );
    }

    if (fermentationInterrupted) {
      return (
        <StatusFermentationInterrupted
          device={device}
          showEditFermentationModal={showEditFermentationModal}
        />
      );
    }

    if (userAction) {
      return <StatusUserAction sessionId={device.session_id} />;
    }

    if (isActive) {
      return (
        <StatusActive
          currentTemp={device.current_temp}
          targetTemp={device.target_temp}
          sessionId={device.session_id}
          processType={device.process_type}
        />
      );
    }

    if (!isKeg && needsAcidCleaning) {
      return (
        <StatusNeedsAcidCleaning
          showModal={showAcidSupportModal}
          deviceUUID={device.uuid}
        />
      );
    }

    if (needsCleaning) {
      return (
        <StatusNeedsCleaning
          showModal={showCleaningModal}
          deviceUUID={device.uuid}
        />
      );
    }

    if (isIdle) {
      return !isKeg ? (
        <StatusIdle showModal={showModal} deviceUUID={device.uuid} />
      ) : (
        <StatusIdleKeg />
      );
    }

    return null;
  };

  return (
    <div className="brewery__device">
      <div className="device__header">
        {isKeg && device.beer_srm ? (
          <div
            className="device__image"
            style={{
              background: srmToCss(device.beer_srm),
              borderRadius: "30%",
              width: 60,
              height: 60,
            }}
          />
        ) : (
          <img
            src={device.image}
            className="device__image"
            alt="Minibrew image"
          />
        )}
        <div className="device__name">
          <div className="device__device-name">
            {isKeg
              ? device.title &&
                !device.title.startsWith("Keg ") &&
                device.title !== device.uuid
                ? `${device.title}${device.beer_name ? ` - ${device.beer_name}` : ""}`
                : device.beer_name || device.title
              : device.title}
          </div>
          <div className="device__status-text">{device.sub_title}</div>
        </div>
        <div className="device__menu">
          <DeviceDropdown
            showDeviceModal={showDeviceModal}
            device={device}
            showWifiModal={showWifiModal}
            showCleaningModal={showCleaningModal}
            showStopSessionModal={showStopSessionModal}
            showResetSessionModal={showResetSessionModal}
            showEditFermentationModal={showEditFermentationModal}
            isDisconnected={isDisconnected}
            showUnregisterDeviceModal={showUnregisterDeviceModal}
            brewEligible={brewEligible}
            nextBrewDate={nextBrewDate}
          >
            <button className="device__menu-button">
              <span className="ion-ios-more device__menu-icon" />
            </button>
          </DeviceDropdown>
        </div>
        <div className="clearfix" />
      </div>
      <div className="device__info">
        <div className="row">
          <div className="device__info-labels">
            {!needsCleaning && !isIdle && !isCleaning && (
              <React.Fragment>
                {!isKeg && <p>Name</p>}
                <p>Beerstyle</p>
              </React.Fragment>
            )}
            <p>Stage</p>
            <p>{device.user_action ? "Waiting" : "Time left"}</p>
            <p>Identifier</p>
            {!isKeg && <p>Last CIP</p>}
          </div>
          <div className="device__info-values">
            {!needsCleaning && !isIdle && !isCleaning && (
              <React.Fragment>
                {!isKeg && <p>{device.beer_name || "-"}</p>}
                <p>{device.beer_style || "-"}</p>
              </React.Fragment>
            )}
            <p>
              {device.stage}
              {device.target_temp && !isIdleProcess
                ? ` (${device.target_temp}°C)`
                : ""}
            </p>
            <p style={{ fontFamily: "Gotham-Bold" }}>
              {!isIdleProcess || device.status_time > 0
                ? moment.duration(device.status_time, "seconds").humanize()
                : "-"}
            </p>
            <p>{device.uuid}</p>
            {!isKeg && (
              <p>
                {device.last_cip_date
                  ? moment(device.last_cip_date).format("MMM D, YYYY")
                  : "-"}
              </p>
            )}
          </div>
        </div>
        <div className="clearfix" />
      </div>
      {renderStatusFooter()}
    </div>
  );
};

export default DeviceItem;
